/**
 * Treasury transparency + purchase confirmation surfaces.
 *
 * - `useTreasuryGuard()` validates SFN_TREASURY_ADDRESS (offline structure +
 *   on-chain owner/cluster) and gates the MEAL2 / MEAL4 purchase buttons.
 * - `<TreasuryTransparency />` renders the public disclosure row (short
 *   address, full address on hover, explorer link, live status badge).
 * - `<PurchaseConfirmModal />` shows the settlement receipt: transaction
 *   hash, destination, and an explorer deep-link.
 */
import { useEffect, useRef, useState } from "react";
import {
  Check,
  Copy,
  Download,
  ExternalLink,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  SignalLow,
  X,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { SFN_TREASURY_ADDRESS, shortTreasury } from "@/lib/ecosystem-constants";
import {
  canPurchase,
  explorerAddressUrl,
  explorerTxUrl,
  receiptFileName,
  receiptToJson,
  SOLANA_CLUSTER,
  verifyTreasuryOnChain,
  type PurchaseReceipt,
  type TreasuryStatus,
} from "@/lib/treasury";
import { track } from "@/lib/analytics";
import { attributionProps } from "@/lib/purchase-attribution";
import { useModalA11y } from "@/hooks/use-modal-a11y";
import { VerifyReceiptTrigger } from "@/components/VerifyReceipt";
import { cn } from "@/lib/utils";

/* ─────────────────────────────── guard hook ─────────────────────────────── */

export function useTreasuryGuard(address: string = SFN_TREASURY_ADDRESS) {
  const [status, setStatus] = useState<TreasuryStatus>("checking");
  useEffect(() => {
    let cancelled = false;
    verifyTreasuryOnChain(address).then((res) => {
      if (!cancelled) setStatus(res.status);
    });
    return () => {
      cancelled = true;
    };
  }, [address]);
  return { status, address, enabled: canPurchase(status), cluster: SOLANA_CLUSTER };
}

/* ──────────────────────────── transparency row ──────────────────────────── */

export function TreasuryTransparency({
  status,
  className,
}: {
  status: TreasuryStatus;
  className?: string;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(SFN_TREASURY_ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable — address is still visible on hover */
    }
  };

  const badge =
    status === "checking"
      ? { label: t("treasury.status.checking"), color: "oklch(0.85 0.03 90)", Icon: Loader2 }
      : status === "verified"
        ? { label: t("treasury.status.verified"), color: "oklch(0.86 0.16 165)", Icon: ShieldCheck }
        : status === "degraded"
          ? {
              label: t("treasury.status.degraded"),
              color: "oklch(0.86 0.14 90)",
              Icon: ShieldCheck,
            }
          : {
              label: t("treasury.status.invalid"),
              color: "oklch(0.70 0.20 25)",
              Icon: ShieldAlert,
            };

  return (
    <section
      aria-label={t("treasury.title")}
      data-testid="treasury-transparency"
      className={cn("rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5", className)}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-saffron">
            {t("treasury.eyebrow")}
          </div>
          <h3 className="mt-1 font-display text-base font-semibold tracking-tight">
            {t("treasury.title")}
          </h3>
        </div>
        <span
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em]"
          style={{ borderColor: `${badge.color.replace(")", " / 0.4)")}`, color: badge.color }}
          role="status"
        >
          <badge.Icon
            className={cn("h-3 w-3", status === "checking" && "animate-spin")}
            aria-hidden
          />
          {badge.label}
        </span>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{t("treasury.desc")}</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t("treasury.label")}
        </span>
        <code
          title={SFN_TREASURY_ADDRESS}
          aria-label={SFN_TREASURY_ADDRESS}
          data-full-address={SFN_TREASURY_ADDRESS}
          className="rounded-md border border-white/10 bg-black/30 px-2 py-1 font-mono text-xs text-foreground/85"
        >
          {shortTreasury(SFN_TREASURY_ADDRESS)}
        </code>
        <button
          type="button"
          onClick={copy}
          aria-label={t("treasury.copy")}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground transition hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
        >
          {copied ? (
            <Check className="h-3 w-3" aria-hidden />
          ) : (
            <Copy className="h-3 w-3" aria-hidden />
          )}
          {copied ? t("treasury.copied") : t("treasury.copy")}
        </button>
        <a
          href={explorerAddressUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground transition hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
        >
          {t("treasury.explorer")}
          <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70">
          {t("treasury.network")}: {SOLANA_CLUSTER}
        </span>
      </div>
    </section>
  );
}

/* ───────────────────────── purchase confirmation ───────────────────────── */

export function PurchaseConfirmModal({
  receipt,
  onClose,
}: {
  receipt: PurchaseReceipt | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useModalA11y({
    open: !!receipt,
    onClose,
    analyticsId: "purchase_receipt",
    initialFocusRef: closeRef,
  });
  const [copied, setCopied] = useState(false);
  // Idempotency guard: one report per signature per mounted modal (protects
  // against effect re-runs / StrictMode double invocation). Receipt ids are
  // deterministic server-side, so a repeat view never creates a new receipt.
  const reportedReceipts = useRef<Set<string>>(new Set());

  // End-to-end conversion: one report per settled purchase, tied to a
  // server-side on-chain confirmation of the signature. The receipt id is a
  // deterministic digest, so retries never create a duplicate receipt.

  useEffect(() => {
    if (!receipt) return;
    if (reportedReceipts.current.has(receipt.signature)) return;
    reportedReceipts.current.add(receipt.signature);
    let cancelled = false;
    const base = {
      token: receipt.token,
      cluster: receipt.cluster,
      ...attributionProps(receipt.token as "MEAL2" | "MEAL4"),
    };
    void (async () => {
      try {
        // The confirmation is best-effort with a hard deadline: a slow or
        // unreachable RPC must never swallow the conversion event.
        const verify = (async () => {
          const { confirmReceipt } = await import("@/lib/receipt.functions");
          return confirmReceipt({ data: { signature: receipt.signature } });
        })();
        const res = await Promise.race([
          verify,
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
        ]);
        if (cancelled) return;
        track("receipt_viewed", {
          ...base,
          verified: res ? res.verified : false,
          ...(res ? { receipt_id: res.receiptId } : {}),
          chain_status: res ? res.status : "unchecked",
        });
      } catch {
        if (!cancelled)
          track("receipt_viewed", { ...base, verified: false, chain_status: "unchecked" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [receipt]);

  const copyHash = async () => {
    if (!receipt) return;
    try {
      await navigator.clipboard?.writeText(receipt.signature);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable — signature stays selectable in the DOM */
    }
    track("receipt_copy_hash", {
      token: receipt.token,
      cluster: receipt.cluster,
      ...attributionProps(receipt.token as "MEAL2" | "MEAL4"),
    });
  };

  const downloadJson = () => {
    if (!receipt) return;
    try {
      const blob = new Blob([receiptToJson(receipt)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = receiptFileName(receipt);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      /* download unsupported in this environment */
    }
    track("receipt_download", {
      token: receipt.token,
      cluster: receipt.cluster,
      ...attributionProps(receipt.token as "MEAL2" | "MEAL4"),
    });
  };

  if (!receipt) return null;

  const rows: { label: string; value: string; mono?: boolean }[] = [
    { label: t("receipt.amount"), value: `${receipt.payAmount} ${receipt.payCurrency}` },
    { label: t("receipt.received"), value: `${receipt.receiveAmount} ${receipt.token}` },
    { label: t("receipt.destination"), value: receipt.destination, mono: true },
    { label: t("receipt.network"), value: receipt.cluster, mono: true },
  ];

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="purchase-receipt-title"
        data-testid="purchase-receipt"
        className="w-full max-w-md rounded-3xl border border-white/10 bg-obsidian/95 p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-saffron">
              {t("receipt.eyebrow")}
            </div>
            <h2
              id="purchase-receipt-title"
              className="mt-1 font-display text-xl font-semibold tracking-tight"
            >
              {t("receipt.title")}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t("receipt.close")}
            className="rounded-full border border-white/10 p-1.5 text-muted-foreground transition hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("receipt.desc")}</p>

        <dl className="mt-5 space-y-2">
          {rows.map((r) => (
            <div
              key={r.label}
              className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"
            >
              <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                {r.label}
              </dt>
              <dd
                className={cn("truncate text-xs text-foreground/90", r.mono && "font-mono")}
                title={r.value}
              >
                {r.mono && r.value.length > 20 ? shortTreasury(r.value) : r.value}
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t("receipt.hash")}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={copyHash}
                data-testid="receipt-copy-hash"
                aria-label={t("receipt.copyHash")}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground transition hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
              >
                {copied ? (
                  <Check className="h-3 w-3" aria-hidden />
                ) : (
                  <Copy className="h-3 w-3" aria-hidden />
                )}
                {copied ? t("receipt.hashCopied") : t("receipt.copyHash")}
              </button>
              <button
                type="button"
                onClick={downloadJson}
                data-testid="receipt-download"
                aria-label={t("receipt.download")}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground transition hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
              >
                <Download className="h-3 w-3" aria-hidden />
                {t("receipt.download")}
              </button>
            </div>
          </div>
          <code
            data-testid="receipt-signature"
            className="mt-1 block break-all font-mono text-[11px] leading-relaxed text-foreground/85"
          >
            {receipt.signature}
          </code>
        </div>

        <a
          href={explorerTxUrl(receipt.signature)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold uppercase tracking-wide text-obsidian transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron"
          style={{ background: "linear-gradient(135deg,#FFD66B,#DEFF9A)" }}
        >
          {t("receipt.viewExplorer")}
          <ExternalLink className="h-4 w-4" aria-hidden />
        </a>

        <div className="mt-3 flex justify-center">
          <VerifyReceiptTrigger signature={receipt.signature} />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── network / RPC status indicator ─────────────────── */

/**
 * Compact live status line rendered directly above the purchase buttons so a
 * degraded / unverified destination is visible at the point of action.
 */
export function NetworkStatusNote({
  status,
  className,
}: {
  status: TreasuryStatus;
  className?: string;
}) {
  const { t } = useI18n();
  if (status === "verified") return null;

  const map = {
    checking: { label: t("rpc.checking"), color: "oklch(0.85 0.03 90)", Icon: Loader2 },
    degraded: { label: t("rpc.degraded"), color: "oklch(0.86 0.14 90)", Icon: SignalLow },
    invalid: { label: t("rpc.invalid"), color: "oklch(0.70 0.20 25)", Icon: ShieldAlert },
  } as const;
  const s = map[status as "checking" | "degraded" | "invalid"];

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="rpc-status"
      data-status={status}
      className={cn(
        "mt-3 flex items-center gap-2 rounded-xl border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em]",
        className,
      )}
      style={{ borderColor: s.color.replace(")", " / 0.35)"), color: s.color }}
    >
      <s.Icon
        className={cn("h-3 w-3 shrink-0", status === "checking" && "animate-spin")}
        aria-hidden
      />
      <span className="leading-relaxed">{s.label}</span>
    </div>
  );
}
